import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Plus, Wrench, MapPin, Calendar, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ConfirmModal from '../components/ConfirmModal';
import axios from 'axios';
import { toast } from 'sonner';
import { trackModuleVisit, trackEvent } from '@/lib/posthog';

const MaintenancePage = () => {
  const { user, selectedBuilding } = useAuth();
  const [maintenanceList, setMaintenanceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    building_id: '',
    title: '',
    description: '',
    location: '',
    priority: 'medium',
    scheduled_date: ''
  });

  // Track module visit on mount
  useEffect(() => {
    trackModuleVisit('mantenimiento');
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    fetchMaintenance();
  }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/buildings`);
      setBuildings(response.data);
      const defaultBuilding = (selectedBuilding && selectedBuilding !== 'all') 
        ? selectedBuilding 
        : (response.data.length > 0 ? response.data[0].id : '');
      setFormData(prev => ({ ...prev, building_id: defaultBuilding }));
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchMaintenance = async () => {
    setLoading(true);
    try {
      const query = selectedBuilding && selectedBuilding !== 'all' ? `?building_id=${selectedBuilding}` : '';
      const response = await axios.get(`${API}/maintenance${query}`);
      setMaintenanceList(response.data);
    } catch (error) {
      console.error('Error fetching maintenance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/maintenance`, formData);
      toast.success('Mantenimiento programado exitosamente');
      // Track maintenance creation
      trackEvent('mantenimiento_created', { building_id: formData.building_id, categoria: formData.priority });
      setDialogOpen(false);
      setFormData({
        building_id: buildings[0]?.id || '',
        title: '',
        description: '',
        location: '',
        priority: 'medium',
        scheduled_date: ''
      });
      fetchMaintenance();
    } catch (error) {
      toast.error('Error al programar mantenimiento');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      scheduled: { label: 'Programado', icon: Calendar, color: 'text-blue-600 bg-blue-50 border-blue-100' },
      in_progress: { label: 'En Proceso', icon: Clock, color: 'text-orange-600 bg-orange-50 border-orange-100' },
      completed: { label: 'Completado', icon: CheckCircle2, color: 'text-green-600 bg-green-50 border-green-100' },
      cancelled: { label: 'Cancelado', icon: XCircle, color: 'text-slate-500 bg-slate-50 border-slate-100' }
    };
    return configs[status] || configs.scheduled;
  };

  const getPriorityBadge = (priority) => {
    const configs = {
      low: { label: 'Baja', color: 'bg-slate-100 text-slate-600' },
      medium: { label: 'Media', color: 'bg-blue-100 text-blue-700' },
      high: { label: 'Alta', color: 'bg-orange-100 text-orange-700' },
      critical: { label: 'Crítica', color: 'bg-red-100 text-red-700 font-bold' }
    };
    const config = configs[priority] || configs.medium;
    return (
      <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const handleCompleteClick = (task) => {
    setTaskToComplete(task);
    setCompleteModalOpen(true);
  };

  const handleCompleteConfirm = async () => {
    if (!taskToComplete) return;
    
    setCompleteLoading(true);
    try {
      await axios.patch(`${API}/maintenance/${taskToComplete.id}`, { status: 'completed' });
      // Optimistic UI update
      setMaintenanceList(prev => 
        prev.map(t => t.id === taskToComplete.id ? { ...t, status: 'completed' } : t)
      );
      toast.success('Tarea marcada como completada');
      trackEvent('mantenimiento_completed', { maintenance_id: taskToComplete.id });
      setCompleteModalOpen(false);
      setTaskToComplete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al completar tarea');
    } finally {
      setCompleteLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900" data-testid="maintenance-page-title">Mantenimiento</h1>
            <p className="text-slate-500 mt-1 text-lg">Programación y estado de reparaciones</p>
          </div>
          {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'staff') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 px-6 h-12" data-testid="create-maintenance-button">
                  <Plus className="h-5 w-5 mr-2" />
                  Nueva Tarea
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]" data-testid="create-maintenance-dialog">
                <DialogHeader>
                  <DialogTitle className="text-xl font-heading font-bold text-slate-900">Programar Mantenimiento</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-5 py-4">
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700">Conjunto</Label>
                    <Select value={formData.building_id} onValueChange={(value) => setFormData({...formData, building_id: value})}>
                      <SelectTrigger className="h-11 bg-slate-50 border-slate-200" data-testid="maintenance-building-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {buildings.map(building => (
                          <SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700">Título de la tarea</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      className="h-11 bg-slate-50 border-slate-200"
                      placeholder="Ej: Reparación ascensor torre B"
                      data-testid="maintenance-title-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700">Descripción</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                      rows={3}
                      className="bg-slate-50 border-slate-200 resize-none"
                      placeholder="Detalles técnicos del trabajo..."
                      data-testid="maintenance-description-input"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label className="text-sm font-semibold text-slate-700">Ubicación</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                      <Input
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        required
                        className="pl-10 h-11 bg-slate-50 border-slate-200"
                        placeholder="Ej: Sótano 1, Cuarto de bombas"
                        data-testid="maintenance-location-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label className="text-sm font-semibold text-slate-700">Prioridad</Label>
                      <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                        <SelectTrigger className="h-11 bg-slate-50 border-slate-200" data-testid="maintenance-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Baja</SelectItem>
                          <SelectItem value="medium">Media</SelectItem>
                          <SelectItem value="high">Alta</SelectItem>
                          <SelectItem value="critical">Crítica</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label className="text-sm font-semibold text-slate-700">Fecha Programada</Label>
                      <Input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                        className="h-11 bg-slate-50 border-slate-200"
                        data-testid="maintenance-date-input"
                      />
                    </div>
                  </div>
                  <div className="pt-2">
                    <Button type="submit" className="w-full h-12 bg-primary hover:bg-primary/90 text-lg font-semibold rounded-xl" data-testid="maintenance-submit-button">
                      Programar Tarea
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Maintenance Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {maintenanceList.length === 0 ? (
            <div className="col-span-full py-16 text-center bg-white rounded-2xl border border-dashed border-slate-200">
              <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench className="h-8 w-8 text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Sin tareas pendientes</h3>
              <p className="text-slate-500 mt-1">Todo el mantenimiento está al día.</p>
            </div>
          ) : (
            maintenanceList.map((task) => {
              const status = getStatusConfig(task.status);
              const isCompleted = task.status === 'completed';
              
              return (
                <Card 
                  key={task.id} 
                  className={`group hover:border-amber-200 hover:shadow-premium transition-all duration-300 overflow-hidden ${isCompleted ? 'opacity-60' : ''}`}
                  data-testid={`maintenance-item-${task.id}`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center bg-slate-50 text-slate-600 group-hover:bg-amber-50 group-hover:text-amber-600 transition-colors`}>
                        <Wrench className="h-5 w-5" />
                      </div>
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${status.color}`}>
                        <status.icon className="h-3.5 w-3.5" />
                        {status.label}
                      </div>
                    </div>
                    
                    <h3 className={`text-lg font-bold mb-2 line-clamp-1 transition-colors ${isCompleted ? 'text-slate-500 line-through' : 'text-slate-900 group-hover:text-amber-700'}`}>
                      {task.title}
                    </h3>
                    
                    <p className="text-slate-500 text-sm line-clamp-2 leading-relaxed mb-4 h-10">
                      {task.description}
                    </p>

                    <div className="space-y-3 pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span className="truncate">{task.location}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        {task.scheduled_date ? (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span>{new Date(task.scheduled_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Sin fecha</span>
                        )}
                        
                        {getPriorityBadge(task.priority)}
                      </div>

                      {/* Complete button for admin/staff on non-completed tasks */}
                      {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'staff') && !isCompleted && (
                        <Button
                          onClick={() => handleCompleteClick(task)}
                          className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`complete-maintenance-${task.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Marcar Completada
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Complete Confirmation Modal */}
        <ConfirmModal
          open={completeModalOpen}
          onOpenChange={setCompleteModalOpen}
          title="Completar Tarea"
          description={`¿Confirmas que la tarea "${taskToComplete?.title}" ha sido completada?`}
          confirmText="Sí, Completar"
          onConfirm={handleCompleteConfirm}
          loading={completeLoading}
          variant="default"
        />
      </div>
    </DashboardLayout>
  );
};

export default MaintenancePage;
